import { BackendFetch } from '@hantera/portal-app'
import type {
  JobSpanDetail,
  RequestDetail,
  SendingSpanDetail,
  TraceDetail,
  TraceListPage,
  TraceListParams,
} from './traces.types'

const isoInstant = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/

/**
 * Dates are revived; numbers are deliberately left as numbers.
 *
 * The shared `parse-json` helper converts every number to `Decimal`, which is right for money and
 * wrong here: durations feed arithmetic in the trace graph and the chart series, and `ChartPoint`
 * is typed `number | null`.
 */
function parseTraceJson(text: string): any {
  return JSON.parse(text, (_key, value) =>
    typeof value === 'string' && isoInstant.test(value) ? new Date(value) : value,
  )
}

export default function traces(fetch: BackendFetch) {
  return {
    /**
     * Returns the whole page rather than just its items: `hasMore` is the only sound stop
     * condition, and a caller left to guess from `items.length` will either stop early or page
     * forever.
     */
    async list(params?: TraceListParams): Promise<TraceListPage> {
      const query = new URLSearchParams()

      if (params?.from) query.set('from', params.from.toISOString())
      if (params?.to) query.set('to', params.to.toISOString())
      if (params?.status) query.set('status', params.status)
      if (params?.traceId) query.set('traceId', params.traceId)
      if (params?.rootOnly) query.set('rootOnly', 'true')
      if (params?.cursor) query.set('cursor', params.cursor)
      if (params?.limit != null) query.set('limit', String(params.limit))

      // Repeated rather than joined: the API binds `resource` as an array, and a resource path may
      // itself contain characters a separator would be ambiguous with.
      for (const resource of params?.resource ?? []) {
        query.append('resource', resource)
      }

      const queryString = query.toString()
      const response = await fetch(
        queryString ? `/core/resources/traces?${queryString}` : '/core/resources/traces',
        { method: 'GET' },
      )

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      const page = parseTraceJson(await response.text())

      return {
        items: page.items ?? [],
        nextCursor: page.nextCursor ?? undefined,
        hasMore: page.hasMore ?? false,
      }
    },


    async get(traceId: string): Promise<TraceDetail> {
      const response = await fetch(`/core/resources/traces/${encodeURIComponent(traceId)}`, {
        method: 'GET',
      })

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      return parseTraceJson(await response.text())
    },

    /**
     * Returns undefined on 404, which is an ordinary outcome rather than an error: the payload may
     * have expired, been logged at level `off`, or lie outside the caller's permissions. The API
     * deliberately does not distinguish those three.
     *
     * `frameOffset` pages an ingress request's response frames, which is how a live stream is
     * followed: only the frames beyond the ones already held are fetched.
     */
    async getRequest(
      traceId: string,
      requestId: string,
      frameOffset = 0,
    ): Promise<RequestDetail | undefined> {
      const query = frameOffset > 0 ? `?frameOffset=${frameOffset}` : ''

      const response = await fetch(
        `/core/resources/traces/${encodeURIComponent(traceId)}/requests/${encodeURIComponent(requestId)}${query}`,
        { method: 'GET' },
      )

      if (response.status === 404) {
        return undefined
      }

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      return parseTraceJson(await response.text())
    },

    /** @see getRequest for why 404 is an outcome rather than an error. */
    async getJob(traceId: string, jobId: string): Promise<JobSpanDetail | undefined> {
      const response = await fetch(
        `/core/resources/traces/${encodeURIComponent(traceId)}/jobs/${encodeURIComponent(jobId)}`,
        { method: 'GET' },
      )

      if (response.status === 404) {
        return undefined
      }

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      return parseTraceJson(await response.text())
    },

    /** @see getRequest for why 404 is an outcome rather than an error. */
    async getSending(traceId: string, sendingId: string): Promise<SendingSpanDetail | undefined> {
      const response = await fetch(
        `/core/resources/traces/${encodeURIComponent(traceId)}/sendings/${encodeURIComponent(sendingId)}`,
        { method: 'GET' },
      )

      if (response.status === 404) {
        return undefined
      }

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      return parseTraceJson(await response.text())
    },
  }
}
